// src/components/employee/EmployeeDocumentsEnhanced.js
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
    FileText, Upload, Download, Eye, Trash2, Plus, AlertCircle,
    Mail, Calendar, CheckCircle, Clock, Filter, Search, X
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../services/api';
import documentService from '../../services/document.service';
import documentDatabaseService from '../../services/documentDatabase.service';
import { getCategoryDisplayName } from '../../utils/categoryHelper';
import debugDocuments from '../../utils/debugDocuments';
import { ensureUserIdConsistency } from '../../utils/setupTestEmployees';
import { getEmployeeDocuments, synchronizeEmployeeIds, debugDocumentStorage } from '../../utils/documentHelpers';
import { fixDocumentVisibility, createUniversalDocumentStore } from '../../utils/fixDocumentIssue';
// import { applyUniversalDocumentFix } from '../../utils/universalDocumentFix'; // DISABLED - Using mock API instead
import '../../utils/debugDocumentDelivery'; // Load debug utility

const EmployeeDocumentsEnhanced = ({ employeeId: propEmployeeId, isAdmin = false }) => {
    const { user } = useAuth();
    const employeeId = propEmployeeId || user?.employee_id || user?.id;

    // State for documents
    const [uploadedDocuments, setUploadedDocuments] = useState([]);
    const [receivedDocuments, setReceivedDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('received'); // 'received' or 'uploaded'
    const tabLocation = useLocation();
    useEffect(() => {
        const params = new URLSearchParams(tabLocation.search);
        const tab = params.get('tab');
        if (tab) setActiveTab(tab);
    }, [tabLocation.search]);

    // Modal and UI states
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [previewDocument, setPreviewDocument] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);

    // Filter and search
    const [searchQuery, setSearchQuery] = useState('');
    const [filterType, setFilterType] = useState('all');

    // Document types
    const documentTypes = [
        'Appointment Letter',
        'Confirmation Letter',
        'Experience Letter',
        'Payslip',
        'Promotion Letter',
        'Termination Letter',
        'Resume',
        'Education Certificate',
        'Identity Proof',
        'Address Proof',
        'Bank Details',
        'Medical Certificate',
        'Background Check',
        'Other'
    ];

    // Fetch documents on component mount
    useEffect(() => {
        // DISABLED: Apply universal document fix - Using mock API instead
        // applyUniversalDocumentFix();

        // Fix document visibility issues
        const fixResult = fixDocumentVisibility();
        console.log('Document visibility fix result:', fixResult);

        // Create universal store as backup
        createUniversalDocumentStore();

        // Synchronize employee IDs first
        const syncedUser = synchronizeEmployeeIds();

        // Run debug diagnostics
        console.log('=== Running Document System Diagnostics ===');
        debugDocumentStorage();

        // Additional specific debug for this component
        console.log('=== EmployeeDocumentsEnhanced Component Info ===');
        console.log('Props employeeId:', propEmployeeId);
        console.log('User from auth:', user);
        console.log('Synced user:', syncedUser);
        console.log('Computed employeeId:', employeeId);

        fetchAllDocuments();
    }, [employeeId, user]);

    const fetchAllDocuments = async () => {
        console.log('🚀 Starting fetchAllDocuments...');
        console.log('🔍 Current employeeId:', employeeId);
        console.log('🔍 Current user:', user);

        setLoading(true);
        setError(null);
        try {
            console.log('📥 Fetching both uploaded and received documents in parallel...');

            // Fetch both uploaded and received documents
            const [uploadedResult, receivedResult] = await Promise.all([
                fetchUploadedDocuments(),
                fetchReceivedDocuments()
            ]);

            console.log('📊 Results received:', {
                uploaded: uploadedResult?.length || 0,
                received: receivedResult?.length || 0
            });

            setUploadedDocuments(uploadedResult || []);
            setReceivedDocuments(receivedResult || []);

            console.log('✅ fetchAllDocuments completed successfully');
        } catch (error) {
            console.error('❌ Error in fetchAllDocuments:', error);
            console.error('❌ Error details:', {
                message: error.message,
                stack: error.stack
            });

            // Don't show error if it's just "no documents found" (e.g., 404 or empty response)
            // Only show error for actual network/server failures
            if (error.response && (error.response.status === 404 || error.response.status === 204)) {
                // No documents found - this is normal, just show empty state
                console.log('No documents found for user (404/204) - showing empty state');
                setUploadedDocuments([]);
                setReceivedDocuments([]);
            } else if (error.message && error.message.includes('Not Found')) {
                // Another form of "no documents" - show empty state
                console.log('No documents found - showing empty state');
                setUploadedDocuments([]);
                setReceivedDocuments([]);
            } else {
                // Actual error - show error message
                setError('Failed to fetch documents');
            }
        } finally {
            setLoading(false);
            console.log('🏁 fetchAllDocuments finished (loading set to false)');
        }
    };

    const fetchUploadedDocuments = async () => {
        try {
            console.log('📡 Fetching uploaded documents from API...');
            console.log('🔍 Calling documentService.getMyDocuments()');

            // Use the document service to fetch uploaded documents via API
            const result = await documentService.getMyDocuments();

            console.log('🔍 getMyDocuments() result:', result);
            console.log('🔍 Result type:', typeof result);
            console.log('🔍 Result.success:', result?.success);
            console.log('🔍 Result.documents:', result?.documents);

            if (result.success && result.documents) {
                console.log(`✅ Successfully fetched ${result.documents.length} uploaded documents from API`);
                return result.documents;
            } else {
                console.warn('⚠️ API returned unexpected result for uploaded documents:', result);
                return [];
            }

        } catch (error) {
            console.error('❌ Error fetching uploaded documents from API:', error);
            console.error('❌ Error message:', error.message);
            console.error('❌ Error stack:', error.stack);
            return [];
        }
    };

    const fetchReceivedDocuments = async () => {
        try {
            const endpoint = `/api/documents/employees/${employeeId}/company-documents`;
            console.log('📡 Fetching received company documents from API...');
            console.log('🔍 Employee ID:', employeeId);
            console.log('🔍 Endpoint:', endpoint);
            console.log('🔍 API Base URL:', process.env.REACT_APP_API_URL || 'http://localhost:5001');
            console.log('🔍 Full URL will be:', (process.env.REACT_APP_API_URL || 'http://localhost:5001') + endpoint);
            console.log('🔍 Token exists:', !!localStorage.getItem('authToken'));

            // IMPORTANT: Use axios api instance with configured baseURL, not native fetch!
            const response = await api.get(endpoint);

            console.log('✅ Response received:', response);
            console.log('🔍 Response status:', response.status);
            console.log('🔍 Response data:', response.data);

            const documents = response.data.documents || [];

            console.log(`✅ Successfully fetched ${documents.length} company documents from API`);
            console.log('📄 Documents:', documents);
            return documents;

        } catch (error) {
            console.error('❌ Error fetching received documents from API:', error);
            console.error('❌ Error message:', error.message);
            console.error('❌ Error response:', error.response);
            console.error('❌ Error config:', error.config);
            setError('Failed to fetch documents. Please try again later.');
            return [];
        }
    };

    const handleUpload = async (file, documentType) => {
        setUploading(true);
        setError(null);
        try {
            console.log('📤 Uploading document to API...');

            // Upload document via API
            const result = await documentService.uploadDocument(file, employeeId, documentType);

            if (result.success) {
                console.log('✅ Document uploaded successfully');
                setShowUploadModal(false);

                // Refresh the documents list
                await fetchAllDocuments();
            } else {
                throw new Error(result.error || 'Upload failed');
            }

        } catch (error) {
            console.error('❌ Upload error:', error);
            setError(error.message || 'Failed to upload document');
        } finally {
            setUploading(false);
        }
    };

    const handleDownload = async (doc) => {
        try {
            console.log('⬇️ Downloading document:', doc);

            // If document has a PDF URL, download that directly
            if (doc.pdfUrl || doc.pdf_url || doc.url) {
                const pdfUrl = doc.pdfUrl || doc.pdf_url || doc.url;
                const fileName = `${getDocumentDisplayName(doc) || 'document'}.pdf`;

                console.log('📥 Downloading PDF from URL:', pdfUrl);

                // Create a temporary link and trigger download
                const link = window.document.createElement('a');
                link.href = pdfUrl;
                link.download = fileName;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                window.document.body.appendChild(link);
                link.click();
                window.document.body.removeChild(link);

                console.log('✅ Download initiated');
                return;
            }

            // Fallback: create HTML blob from content
            console.log('📄 Creating HTML blob from content');
            const content = doc.content || `
                Document: ${getDocumentDisplayName(doc)}
                Type: ${doc.category || doc.document_type}
                Generated: ${doc.generatedAt || doc.receivedAt || doc.uploaded_at}

                This is a sample document content.
                ${doc.content || ''}
            `;

            const blob = new Blob([content], { type: 'text/html' });
            const url = URL.createObjectURL(blob);
            const link = window.document.createElement('a');
            link.href = url;
            link.download = `${getDocumentDisplayName(doc)}.html`;
            window.document.body.appendChild(link);
            link.click();
            window.document.body.removeChild(link);
            URL.revokeObjectURL(url);

            console.log('✅ HTML download completed');
        } catch (error) {
            console.error('❌ Download error:', error);
            setError('Failed to download document');
        }
    };

    const handleDelete = async (documentId, isReceived = false) => {
        if (!window.confirm('Are you sure you want to delete this document?')) return;

        try {
            if (isReceived) {
                const updatedDocs = receivedDocuments.filter(doc => doc.id !== documentId);
                setReceivedDocuments(updatedDocs);
                localStorage.setItem(`employeeDocuments_${employeeId}`, JSON.stringify(updatedDocs));
            } else {
                const updatedDocs = uploadedDocuments.filter(doc => doc.id !== documentId);
                setUploadedDocuments(updatedDocs);
                localStorage.setItem(`uploadedDocs_${employeeId}`, JSON.stringify(updatedDocs));
            }
        } catch (error) {
            console.error('Delete error:', error);
            setError('Failed to delete document');
        }
    };

    const handlePreview = (doc) => {
        try {
            console.log('👁️ Previewing document:', doc);

            // If document has a PDF URL AND it's a valid URL, open that directly
            const pdfUrl = doc.pdfUrl || doc.pdf_url || doc.url || doc.firebaseUrl || doc.document_url;

            // Validate URL - must be a proper HTTP(S) URL or blob URL, not empty/HTML content
            const isValidUrl = pdfUrl &&
                               pdfUrl.trim() !== '' &&
                               (pdfUrl.startsWith('http://') ||
                                pdfUrl.startsWith('https://') ||
                                pdfUrl.startsWith('blob:')) &&
                               !pdfUrl.includes('<') && // Not HTML content
                               !pdfUrl.includes('function') && // Not JavaScript
                               pdfUrl.length < 2000; // Reasonable URL length

            if (isValidUrl) {
                console.log('🔗 Opening valid PDF URL:', pdfUrl);
                window.open(pdfUrl, '_blank', 'noopener,noreferrer');
                console.log('✅ Preview opened in new tab');
                return;
            }

            // Fallback: show in modal only if no valid PDF URL
            console.log('📋 No valid PDF URL, showing in preview modal');
            setPreviewDocument(doc);
        } catch (error) {
            console.error('❌ Preview error:', error);
            setError('Failed to preview document');
        }
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return 'N/A';
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        if (bytes === 0) return '0 Bytes';
        const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
        return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
    };

    // Removed duplicate getCategoryName function - using getCategoryDisplayName from categoryHelper.js instead

    // Helper to extract category from template name when category is null
    const extractCategoryFromTemplate = (templateName) => {
        if (!templateName) return null;

        // Common patterns: "Modern & Concise Appointment Letter", "Standard Payslip", etc.
        const categoryMap = {
            'appointment': 'appointment',
            'payslip': 'payslip',
            'termination': 'termination',
            'promotion': 'promotion',
            'experience': 'experience',
            'confirmation': 'confirmation',
            'increment': 'increment',
            'transfer': 'transfer',
            'resignation': 'resignation',
            'offer': 'offer',
            'warning': 'warning',
            'appreciation': 'appreciation',
            'relieving': 'relieving',
            'internship': 'internship',
            'contract': 'contract',
            'noc': 'noc',
            'bonafide': 'bonafide',
            'salary': 'salary'
        };

        const lowerTemplate = templateName.toLowerCase();
        for (const [key, value] of Object.entries(categoryMap)) {
            if (lowerTemplate.includes(key)) {
                return value;
            }
        }
        return null;
    };

    // Get display name for document with fallback to extracting from template
    const getDocumentDisplayName = (doc) => {
        console.log('🔍 getDocumentDisplayName:', {
            category: doc.category,
            document_type: doc.document_type,
            template_name: doc.template_name,
            document_name: doc.document_name
        });

        // Try category or document_type first (category is stored in document_type for some documents)
        const categoryValue = doc.category || doc.document_type;
        if (categoryValue) {
            const displayName = getCategoryDisplayName(categoryValue);
            console.log('✅ Using category/document_type:', categoryValue, '→', displayName);
            return displayName;
        }

        // If no category, try to extract from template_name or document_name
        const extractedCategory = extractCategoryFromTemplate(doc.template_name || doc.document_name);
        if (extractedCategory) {
            const displayName = getCategoryDisplayName(extractedCategory);
            console.log('✅ Extracted category:', extractedCategory, '→', displayName);
            return displayName;
        }

        // If extraction failed, try to clean up template format words from document_name
        if (doc.document_name) {
            // Remove common template format words - more aggressive patterns
            let cleanName = doc.document_name
                .replace(/Modern\s*&\s*Concise\s*/gi, '')
                .replace(/Modern\s*and\s*Concise\s*/gi, '')
                .replace(/Standard\s*/gi, '')
                .replace(/Professional\s*/gi, '')
                .replace(/Formal\s*/gi, '')
                .replace(/Basic\s*/gi, '')
                .replace(/Simple\s*/gi, '')
                .replace(/Classic\s*/gi, '')
                .trim();

            console.log('🧹 Cleaned name:', doc.document_name, '→', cleanName);
            return cleanName || doc.document_name;
        }

        // Final fallback
        console.log('⚠️ Using fallback: Document');
        return 'Document';
    };

    // Filter documents based on search and filter
    const filterDocuments = (docs) => {
        let filtered = docs;

        // Apply search filter
        if (searchQuery) {
            filtered = filtered.filter(doc => {
                const name = (getDocumentDisplayName(doc) || '').toLowerCase();
                const type = (doc.document_type || getDocumentDisplayName(doc) || '').toLowerCase();
                return name.includes(searchQuery.toLowerCase()) || type.includes(searchQuery.toLowerCase());
            });
        }

        // Apply type filter
        if (filterType !== 'all') {
            filtered = filtered.filter(doc => {
                const docType = doc.document_type || getDocumentDisplayName(doc);
                return docType === filterType;
            });
        }

        return filtered;
    };

    const currentDocuments = activeTab === 'received'
        ? filterDocuments(receivedDocuments)
        : filterDocuments(uploadedDocuments);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    HR Documents
                </h2>
                {!isAdmin && (
                    <button
                        onClick={() => setShowUploadModal(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                    >
                        <Upload className="w-4 h-4" />
                        Upload Document
                    </button>
                )}
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                    <div>
                        <h3 className="font-semibold text-red-800 dark:text-red-300">Error</h3>
                        <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div className="border-b border-gray-200 dark:border-gray-700">
                <div className="flex gap-6">
                    <button
                        onClick={() => setActiveTab('received')}
                        className={`pb-2 px-1 font-medium transition-colors relative ${
                            activeTab === 'received'
                                ? 'text-blue-600 dark:text-blue-400'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300'
                        }`}
                    >
                        Company Documents
                        {receivedDocuments.length > 0 && (
                            <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                                {receivedDocuments.length}
                            </span>
                        )}
                        {activeTab === 'received' && (
                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400" />
                        )}
                    </button>
                    <button
                        onClick={() => setActiveTab('uploaded')}
                        className={`pb-2 px-1 font-medium transition-colors relative ${
                            activeTab === 'uploaded'
                                ? 'text-blue-600 dark:text-blue-400'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300'
                        }`}
                    >
                        My Documents
                        {uploadedDocuments.length > 0 && (
                            <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                {uploadedDocuments.length}
                            </span>
                        )}
                        {activeTab === 'uploaded' && (
                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400" />
                        )}
                    </button>
                </div>
            </div>

            {/* Search and Filter */}
            <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search documents..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>
                <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                    <option value="all">All Types</option>
                    {documentTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                    ))}
                </select>
            </div>

            {/* Documents Grid */}
            {currentDocuments.length === 0 ? (
                <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg">
                    <FileText className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        No {activeTab === 'received' ? 'Company' : 'Uploaded'} Documents
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400">
                        {activeTab === 'received'
                            ? 'Documents sent by your company will appear here'
                            : 'Upload your personal documents to store them securely'}
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {currentDocuments.map((doc) => (
                        <div
                            key={doc.id}
                            className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg p-4 hover:shadow-lg transition-all duration-200"
                        >
                            {/* Document Header */}
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                                        <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    {activeTab === 'received' && (
                                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400">
                                            From Company
                                        </span>
                                    )}
                                </div>
                                {doc.is_verified && (
                                    <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                                )}
                            </div>

                            {/* Document Info */}
                            <h3 className="font-semibold text-gray-900 dark:text-white mb-1 truncate"
                                title={getDocumentDisplayName(doc)}>
                                {getDocumentDisplayName(doc)}
                            </h3>
                            {/* <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                                {doc.document_type || getDocumentDisplayName(doc)}
                            </p> */}

                            {/* Document Metadata */}
                            <div className="space-y-1 mb-4">
                                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                                    <Calendar className="w-3 h-3" />
                                    <span>
                                        {activeTab === 'received' ? 'Received: ' : 'Uploaded: '}
                                        {new Date(doc.receivedAt || doc.uploaded_at || doc.generatedAt || doc.created_at).toLocaleDateString()}
                                    </span>
                                </div>
                                {doc.file_size && (
                                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                                        <FileText className="w-3 h-3" />
                                        <span>Size: {formatFileSize(doc.file_size)}</span>
                                    </div>
                                )}
                                {activeTab === 'received' && doc.sentBy && (
                                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                                        <Mail className="w-3 h-3" />
                                        <span>From: HR Department</span>
                                    </div>
                                )}
                            </div>

                            {/* Action Buttons */}
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => handlePreview(doc)}
                                    className="flex-1 px-3 py-1.5 text-sm bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors flex items-center justify-center gap-1"
                                >
                                    <Eye className="w-4 h-4" />
                                    View
                                </button>
                                <button
                                    onClick={() => handleDownload(doc)}
                                    className="flex-1 px-3 py-1.5 text-sm bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded hover:bg-green-100 dark:hover:bg-green-900/40 transition-colors flex items-center justify-center gap-1"
                                >
                                    <Download className="w-4 h-4" />
                                    Download
                                </button>
                                {activeTab === 'uploaded' && (
                                    <button
                                        onClick={() => handleDelete(doc.id, false)}
                                        className="px-3 py-1.5 text-sm bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Upload Modal */}
            {showUploadModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                                Upload Document
                            </h3>
                            <button
                                onClick={() => setShowUploadModal(false)}
                                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={(e) => {
                            e.preventDefault();
                            const formData = new FormData(e.target);
                            const file = formData.get('file');
                            const type = formData.get('type');
                            handleUpload(file, type);
                        }}>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Document Type
                                    </label>
                                    <select
                                        name="type"
                                        required
                                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    >
                                        <option value="">Select type...</option>
                                        {documentTypes.map(type => (
                                            <option key={type} value={type}>{type}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Select File
                                    </label>
                                    <input
                                        type="file"
                                        name="file"
                                        required
                                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    />
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                        Accepted formats: PDF, DOC, DOCX, JPG, PNG (Max: 10MB)
                                    </p>
                                </div>
                            </div>

                            <div className="flex gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setShowUploadModal(false)}
                                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={uploading}
                                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {uploading ? (
                                        <>
                                            <Clock className="w-4 h-4 animate-spin" />
                                            Uploading...
                                        </>
                                    ) : (
                                        <>
                                            <Upload className="w-4 h-4" />
                                            Upload
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Preview Modal */}
            {previewDocument && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <h3 className="text-xl font-semibold text-gray-900">
                                {getDocumentDisplayName(previewDocument)}
                            </h3>
                            <button
                                onClick={() => setPreviewDocument(null)}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                            {previewDocument.content ? (
                                <>
                                    {console.log('📄 Preview content length:', previewDocument.content.length)}
                                    {console.log('📄 Content starts with:', previewDocument.content.substring(0, 100))}
                                    <div
                                        className="prose max-w-none"
                                        dangerouslySetInnerHTML={{ __html: previewDocument.content }}
                                    />
                                </>
                            ) : (
                                <div className="text-center py-12">
                                    <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                    <p className="text-gray-600">
                                        Document preview not available
                                    </p>
                                    <button
                                        onClick={() => handleDownload(previewDocument)}
                                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 inline-flex items-center gap-2"
                                    >
                                        <Download className="w-4 h-4" />
                                        Download Document
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmployeeDocumentsEnhanced;